import { supabase } from '@/lib/supabaseClient';
import { DiagramModel, DiagramContentModel } from '@/admin/utils/diagramModels';

// Khai báo các key cho cache
export const DIAGRAM_CACHE_KEYS = {
  DIAGRAM: 'cache_diagram_',
  DIAGRAM_CONTENT: 'cache_diagram_content_',
  ALL_DIAGRAMS: 'cache_all_diagrams',
  DIAGRAM_CATEGORIES: 'cache_diagram_categories', // Lưu ý: Bây giờ lấy từ bảng 'categories' với content_type='diagram'
  LAST_UPDATE: 'cache_diagram_last_update',
};

// Thời gian mặc định cho cache (30 phút)
export const DEFAULT_CACHE_TIME = 30 * 60 * 1000;

/**
 * Lấy dữ liệu từ cache
 * @param key Key của cache
 * @returns Dữ liệu từ cache hoặc null nếu không có
 */
export function getCache<T>(key: string): T | null {
  try {
    const cachedData = localStorage.getItem(key);
    if (!cachedData) return null;

    const { data, expiry } = JSON.parse(cachedData);
    
    // Kiểm tra xem cache có hết hạn không
    if (expiry && Date.now() > expiry) {
      localStorage.removeItem(key);
      return null;
    }
    
    return data as T;
  } catch (error) {
    console.error(`Error getting cache for ${key}:`, error);
    return null;
  }
}

/**
 * Lưu dữ liệu vào cache
 * @param key Key của cache
 * @param data Dữ liệu cần lưu
 * @param ttl Thời gian sống của cache (ms)
 */
export function setCache<T>(key: string, data: T, ttl: number = DEFAULT_CACHE_TIME): void {
  try {
    const expiry = Date.now() + ttl;
    localStorage.setItem(key, JSON.stringify({ data, expiry }));
  } catch (error) {
    console.error(`Error setting cache for ${key}:`, error);
  }
}

/**
 * Xóa cache theo key
 * @param key Key của cache
 */
export function clearCache(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error(`Error clearing cache for ${key}:`, error);
  }
}

/**
 * Lấy dữ liệu từ cache hoặc fetch từ callback nếu cache không có hoặc hết hạn
 * @param key Key của cache
 * @param fetchCallback Callback để lấy dữ liệu nếu cache không có
 * @param ttl Thời gian sống của cache (ms)
 * @returns Dữ liệu từ cache hoặc từ callback
 */
export async function getCacheOrFetch<T>(
  key: string,
  fetchCallback: () => Promise<T>,
  ttl: number = DEFAULT_CACHE_TIME
): Promise<T> {
  // Kiểm tra cache
  const cachedData = getCache<T>(key);
  if (cachedData !== null) {
    return cachedData;
  }
  
  // Nếu không có cache, fetch dữ liệu mới
  const data = await fetchCallback();
  
  // Lưu vào cache
  setCache(key, data, ttl);
  
  return data;
}

/**
 * Lấy dữ liệu từ Supabase với cache
 * @param table Tên bảng
 * @param columns Các cột cần lấy
 * @param filters Các điều kiện lọc
 * @param ttl Thời gian sống của cache (ms)
 * @returns Dữ liệu từ cache hoặc từ Supabase
 */
export async function getSupabaseDataWithCache<T>(
  table: string,
  columns: string = '*',
  filters?: Record<string, any>,
  ttl: number = DEFAULT_CACHE_TIME
): Promise<T[]> {
  // Tạo key cache từ tham số
  const filterString = filters ? JSON.stringify(filters) : '';
  const cacheKey = `${table}:${columns}:${filterString}`;
  
  return getCacheOrFetch<T[]>(
    cacheKey,
    async () => {
      let query = supabase.from(table).select(columns);
      
      // Áp dụng các điều kiện lọc
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            query = query.in(key, value);
          } else {
            query = query.eq(key, value);
          }
        });
      }
      
      const { data, error } = await query;
      
      if (error) {
        console.error(`Error fetching data from ${table}:`, error);
        throw error;
      }
      
      return data as T[];
    },
    ttl
  );
}

/**
 * Đồng bộ cache sau khi cập nhật dữ liệu
 * @param diagram Diagram đã cập nhật
 */
export function syncDiagramCache(diagram: DiagramModel): void {
  // Cập nhật thời điểm cập nhật gần nhất
  setCache(DIAGRAM_CACHE_KEYS.LAST_UPDATE, Date.now());
  
  // Xóa cache của diagram cụ thể
  clearCache(`${DIAGRAM_CACHE_KEYS.DIAGRAM}${diagram.id}`);
  clearCache(`${DIAGRAM_CACHE_KEYS.DIAGRAM_CONTENT}${diagram.id}`);
  
  // Xóa cache danh sách tất cả diagrams
  clearCache(DIAGRAM_CACHE_KEYS.ALL_DIAGRAMS);
}

// Các hàm tiện ích cho diagram

export const setDiagramCache = (diagramId: string, diagramData: DiagramModel): void => {
  setCache(`${DIAGRAM_CACHE_KEYS.DIAGRAM}${diagramId}`, diagramData);
};

export const getDiagramCache = (diagramId: string): DiagramModel | null => {
  return getCache<DiagramModel>(`${DIAGRAM_CACHE_KEYS.DIAGRAM}${diagramId}`);
};

export const setDiagramContentCache = (diagramId: string, contentData: DiagramContentModel): void => {
  setCache(`${DIAGRAM_CACHE_KEYS.DIAGRAM_CONTENT}${diagramId}`, contentData);
};

export const getDiagramContentCache = (diagramId: string): DiagramContentModel | null => {
  return getCache<DiagramContentModel>(`${DIAGRAM_CACHE_KEYS.DIAGRAM_CONTENT}${diagramId}`);
};

export const clearDiagramCache = (diagramId: string): void => {
  clearCache(`${DIAGRAM_CACHE_KEYS.DIAGRAM}${diagramId}`);
  clearCache(`${DIAGRAM_CACHE_KEYS.DIAGRAM_CONTENT}${diagramId}`);
};

/**
 * Xóa tất cả cache liên quan đến diagram
 */
export const clearAllDiagramCache = (): void => {
  // Xóa cache của danh sách tất cả diagrams
  clearCache(DIAGRAM_CACHE_KEYS.ALL_DIAGRAMS);
  
  // Xóa cache danh mục diagram
  clearCache(DIAGRAM_CACHE_KEYS.DIAGRAM_CATEGORIES);
  
  // Xóa cache của tất cả các diagram cụ thể
  // Lấy tất cả các key trong localStorage
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    // Nếu key bắt đầu bằng cache_diagram_, xóa nó
    if (key && (key.startsWith(DIAGRAM_CACHE_KEYS.DIAGRAM) || key.startsWith(DIAGRAM_CACHE_KEYS.DIAGRAM_CONTENT))) {
      localStorage.removeItem(key);
    }
  }
  
  // Cập nhật thời điểm làm mới cache
  setCache(DIAGRAM_CACHE_KEYS.LAST_UPDATE, Date.now());
  
  console.log('All diagram cache cleared');
};

/**
 * Kiểm tra xem cache có cần làm mới không
 * @param lastCheck Thời điểm kiểm tra gần nhất
 * @returns true nếu cần làm mới, false nếu không
 */
export function shouldRefreshCache(lastCheck: number): boolean {
  const lastUpdate = getCache<number>(DIAGRAM_CACHE_KEYS.LAST_UPDATE) || 0;
  return lastUpdate > lastCheck;
} 